import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const ContactForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    email: "",
    description: "",
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobilePattern = /^[0-9]{10}$/;

    if (!formData.name.trim()) {
      newErrors.name = t("contact.errors.nameRequired");
    }

    if (!formData.mobile) {
      newErrors.mobile = t("contact.errors.mobileRequired");
    } else if (!mobilePattern.test(formData.mobile)) {
      newErrors.mobile = t("contact.errors.mobileInvalid");
    }

    if (!formData.email) {
      newErrors.email = t("contact.errors.emailRequired");
    } else if (!emailPattern.test(formData.email)) {
      newErrors.email = t("contact.errors.emailInvalid");
    }

    if (!formData.description.trim()) {
      newErrors.description = t("contact.errors.descriptionRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      alert(t("contact.formSuccess"));
      console.log("Form Data:", formData);
      setFormData({
        name: "",
        mobile: "",
        email: "",
        description: "",
      });
      setErrors({});
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-[#f3f4f6] to-[#e5e7eb] p-4">
      <div className="bg-white/95 backdrop-blur-sm shadow-2xl rounded-xl p-6 md:p-8 w-full max-w-md transform transition-all duration-300 hover:scale-105">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
          {t("contactUs")}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Field */}
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("name")}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`mt-1 block w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#6a11cb] focus:outline-none transition-all duration-300 ${
                errors.name
                  ? "border-red-500"
                  : "border-gray-300 hover:border-[#6a11cb]"
              }`}
              placeholder={t("contact.placeholders.name")}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Mobile Field */}
          <div>
            <label
              htmlFor="mobile"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("phone")}
            </label>
            <input
              type="tel"
              id="mobile"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              className={`mt-1 block w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#6a11cb] focus:outline-none transition-all duration-300 ${
                errors.mobile
                  ? "border-red-500"
                  : "border-gray-300 hover:border-[#6a11cb]"
              }`}
              placeholder={t("contact.placeholders.mobile")}
            />
            {errors.mobile && (
              <p className="text-red-500 text-sm mt-1">{errors.mobile}</p>
            )}
          </div>

          {/* Email Field */}
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("email")}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`mt-1 block w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#6a11cb] focus:outline-none transition-all duration-300 ${
                errors.email
                  ? "border-red-500"
                  : "border-gray-300 hover:border-[#6a11cb]"
              }`}
              placeholder={t("contact.placeholders.email")}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Description Field */}
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              {t("message")}
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={`mt-1 block w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#6a11cb] focus:outline-none transition-all duration-300 ${
                errors.description
                  ? "border-red-500"
                  : "border-gray-300 hover:border-[#6a11cb]"
              }`}
              placeholder={t("contact.placeholders.description")}
              rows="4"
            ></textarea>
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">{errors.description}</p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex space-x-4">
            <button
              type="submit"
              className="flex-1 bg-gradient-to-r from-[#6a11cb] to-[#2575fc] text-white py-3 px-4 rounded-lg font-semibold hover:from-[#2575fc] hover:to-[#6a11cb] focus:outline-none focus:ring-2 focus:ring-[#6a11cb] focus:ring-offset-2 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              {t("sendMessage")}
            </button>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex-1 bg-gray-300 text-gray-800 py-3 px-4 rounded-lg font-semibold hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-300"
            >
              {t("cancel")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;
